// src/components/Login.jsx
// Login Page Component

import React, { useState, useEffect } from 'react';
import { login, resetPassword, resendVerificationEmail } from '../firebase/auth';
import { getOrgBranding } from '../firebase/orgBranding';
import { DEFAULT_ORG_ID } from '../firebase/orgs';
import { getRememberedTutor } from '../utils/rememberedTutor';
import { AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';

const Login = ({ onLoginSuccess, onRegisterClick }) => {
  const isMobile = useIsMobile();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resentVerification, setResentVerification] = useState(false);
  // The logo of the tutor whose account last signed in on this device (see
  // rememberedTutor.js): undefined while it loads, null for the generic page
  const [tutorLogoUrl, setTutorLogoUrl] = useState(() => (getRememberedTutor() ? undefined : null));
  // The tutor's header colour: their logo is made to sit on it in the
  // dashboards, and a white logo would vanish on this white card without it
  const [tutorLogoBackground, setTutorLogoBackground] = useState(null);

  useEffect(() => {
    const remembered = getRememberedTutor();
    if (!remembered) return;
    if (remembered.orgId === DEFAULT_ORG_ID) {
      setTutorLogoUrl('/aileen-edgar-logo.png');
      return;
    }
    const brandingRole = { marker: 'marker', head_marker: 'headMarker' }[remembered.role] || 'student';
    getOrgBranding(remembered.orgId).then(branding => {
      setTutorLogoUrl(branding.logos?.[brandingRole]?.url || branding.logoUrl || null);
      setTutorLogoBackground(branding.headerColors?.[brandingRole] || null);
    });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setEmailNotVerified(false);
    setResentVerification(false);
    setLoading(true);

    try {
      const user = await login(email.trim(), password);
      onLoginSuccess(user);
    } catch (err) {
      console.error('Login error:', err);

      // User-friendly error messages. Firebase now returns the single
      // auth/invalid-credential code for both a wrong password and an
      // unrecognised email (it used to be auth/wrong-password /
      // auth/user-not-found) - handle all three the same way rather than
      // falling through to the raw Firebase error text below.
      if (err.code === 'auth/email-not-verified') {
        setEmailNotVerified(true);
      } else if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Invalid email or password');
      } else if (err.code === 'auth/user-disabled') {
        // e.g. their tutor has deleted the account (Recently Deleted)
        setError('This account is not currently active. Please contact your tutor.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await resendVerificationEmail(email.trim(), password);
      setResentVerification(true);
    } catch (err) {
      setError('Could not resend verification email. Please check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email.trim());
      setResetEmailSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f5f1e8 0%, #dbd9db 100%)',
      padding: isMobile ? '12px' : '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        padding: isMobile ? '28px 20px' : '48px',
        maxWidth: '420px',
        width: '100%'
      }}>
        {/* Header - the tutor last signed in on this device, if they have a
            logo, otherwise generic (nothing else says whose account this is
            before sign-in) */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          {tutorLogoUrl === undefined ? (
            <div style={{ height: '80px' }} />
          ) : tutorLogoUrl ? (
            <div style={{
              background: tutorLogoBackground || 'transparent',
              borderRadius: '10px',
              padding: tutorLogoBackground ? '16px' : 0
            }}>
              <img
                src={tutorLogoUrl}
                alt="Your tutor's logo"
                style={{ maxWidth: '100%', maxHeight: '120px', width: 'auto', height: 'auto', display: 'block', margin: '0 auto' }}
              />
            </div>
          ) : (
            <h1 style={{
              margin: '0 0 16px 0',
              fontSize: '28px',
              color: '#000000',
              fontWeight: '700'
            }}>
              Homework Solutions
            </h1>
          )}
          <p style={{
            margin: '8px 0 0 0',
            color: '#718096',
            fontSize: '14px'
          }}>
            {showForgotPassword ? 'Reset your password' : 'Sign in to continue'}
          </p>
        </div>

        {/* Reset Email Sent Message */}
        {resetEmailSent && (
          <div style={{
            background: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            color: '#155724'
          }}>
            <strong>Email sent!</strong>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
              Check your inbox for password reset instructions.
            </p>
          </div>
        )}

        {/* Email Not Verified */}
        {emailNotVerified && (
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            color: '#856404'
          }}>
            <strong>Email not verified</strong>
            <p style={{ margin: '8px 0 12px 0', fontSize: '14px' }}>
              Please click the verification link we emailed you before logging in.
            </p>
            {resentVerification ? (
              <p style={{ margin: 0, fontSize: '14px', color: '#155724', fontWeight: '600' }}>
                Verification email resent — check your inbox.
              </p>
            ) : (
              <button
                onClick={handleResendVerification}
                disabled={loading}
                style={{
                  background: '#ffc107',
                  color: '#212529',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Sending...' : 'Resend verification email'}
              </button>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            color: '#721c24'
          }}>
            <strong>{error}</strong>
          </div>
        )}

        {/* Login Form */}
        {!showForgotPassword ? (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#4a5568',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="your.email@example.com"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#c33329'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#4a5568',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="••••••••"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#c33329'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: loading ? '#a0aec0' : '#c33329',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
                marginBottom: '16px'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#a02b23')}
              onMouseLeave={(e) => !loading && (e.target.style.background = '#c33329')}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                color: '#c33329',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#f7fafc'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
              Forgot password?
            </button>
          </form>
        ) : (
          /* Forgot Password Form */
          <form onSubmit={handleForgotPassword}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: '#4a5568',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="your.email@example.com"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#c33329'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: loading ? '#a0aec0' : '#c33329',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
                marginBottom: '16px'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#a02b23')}
              onMouseLeave={(e) => !loading && (e.target.style.background = '#c33329')}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setResetEmailSent(false);
                setError('');
              }}
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                color: '#c33329',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#f7fafc'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
              Back to sign in
            </button>
          </form>
        )}

        {/* Footer */}
        <div style={{
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #e2e8f0',
          textAlign: 'center',
          color: '#a0aec0',
          fontSize: '12px'
        }}>
          {onRegisterClick && (
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#718096' }}>
              New student?{' '}
              <button
                onClick={onRegisterClick}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#c33329',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Create an account
              </button>
            </p>
          )}
          <p style={{ margin: 0 }}>
            Need help? Contact your administrator
          </p>
          <p style={{ margin: '12px 0 0 0' }}>
            <a href="/terms" style={{ color: '#a0aec0' }}>Terms and Conditions</a>
            {' | '}
            <a href="/privacy" style={{ color: '#a0aec0' }}>Privacy Policy</a>
          </p>
          <div style={{ marginTop: '20px' }}>
            <div style={{ marginBottom: '2px' }}>Powered by</div>
            <img
              src="/homework-solutions-logo.png"
              alt="Homework Solutions"
              style={{ width: '170px', height: 'auto', display: 'block', margin: '0 auto' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;